"use client";

import React, { useState, useEffect } from "react";
import { Row, Col, Card, Button, Form } from "react-bootstrap";
import TextInput from "../../common/FormFields/TextInput.jsx";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from '../../common/Toast/toast';
import { useParams, Link, useNavigate } from "react-router-dom";
import AsyncSelectDropdown from "../../common/CustomAsyncDropdown.jsx";
import { getappstoroles } from "../../../../services/AppToRole.js";
import {
  addUser,
  getUser,
  updateUser,
} from "../../../../services/UserSerivce.js";
import { prefillPassword } from "../../../../utils/passwordField.js";
import "../../common/SettingsStepForm.css";

const WarnIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v5M12 16.5v.01" />
  </svg>
);

/* ── One step ───────────────────────────────────────────────────────
   Unlike the /setting/List-user dialog, which splits into Details and
   Roles, this form has no roles step: every account reached from
   /student-users is a student, and fetchRoles ticks the Student role
   itself on a new one. A picker whose answer is always the same is a
   step that only ever gets clicked past, so the role is shown as a
   read-only line in the details instead -- still visible, just not
   asked. STEP_COPY stays an array because onStepChange indexes it.
   ------------------------------------------------------------------ */
const STEP_COPY = [
  {
    title: "Who is this student?",
    hint: "Their name, how to reach them, and how they sign in.",
  },
];

// Renders as a page on /add-student-user and as the body of a dialog when
// ListStudentUsers opens it. With none of these props it reads the URL and
// navigates on finish, exactly as the route always did.
const AddStudentUser = ({
  // Shown inside a dialog rather than as a page: the component drops its
  // own back-link and page chrome, since the dialog supplies them.
  embedded = false,
  // The record to edit. Falls back to the :id route param.
  recordId,
  // Called after a successful save instead of navigating away.
  onDone,
  // Called when the form is dismissed instead of navigating away.
  onCancel,
  // Told the current step's question, so the dialog can title itself.
  onStepChange,
}) => {
  const { id: routeId } = useParams();
  const id = recordId ?? routeId;
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rolesOptions, setRolesOptions] = useState([]);
  const [filteredRolesOptions, setFilteredRolesOptions] = useState([]);
  const [studentRoleId, setStudentRoleId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [userStatus, setUserStatus] = useState("");

  /* ── Who may set a password ───────────────────────────────────────
     Everyone who reaches this form. Not the admin-only rule the
     /setting/List-user dialog uses: whoever has access to manage these
     students may set their password, whatever their role is called. */
  const maySetPassword = true;

  /* What DEV_MODE put in the box, so an untouched prefill can be told
     from a password that was actually typed. */
  const [prefilledPw, setPrefilledPw] = useState("");

  const navigate = useNavigate();

  // Leaving the form means "go back to the list" on the route, and "close
  // the dialog" when embedded. Every exit goes through these two so the
  // component never has to know which it is.
  const leave = () => {
    if (onCancel) onCancel();
    else navigate("/student-users");
  };

  const finish = () => {
    if (onDone) onDone();
    else navigate("/student-users");
  };

  // Reported once. There is only the one step now, so nothing changes it
  // afterwards -- the dialog just needs its title at the start.
  useEffect(() => {
    if (onStepChange) onStepChange(STEP_COPY[0], 1);
    // onStepChange is the dialog's setState; adding it here would
    // re-report on every render of the list behind it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusOptions = [
    { value: "unverified", label: "Unverified" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const fetchRoles = async () => {
    try {
      const response = await getappstoroles();

      if (Array.isArray(response.data)) {
        const formattedRoles = response.data.map((role) => ({
          label: role.label,
          value: role.value,
          id: role.id,
        }));

        setRolesOptions(formattedRoles);

        const studentRole = formattedRoles.find(
          (r) => r.value === "student" || r.label.toLowerCase() === "student",
        );

        if (studentRole) {
          setStudentRoleId(studentRole.id);

          if (!id) {
            setUserData((prev) => ({
              ...(prev || {}),
              profileIds: [studentRole.id],
            }));
          }
        }

        return formattedRoles;
      }

      setRolesOptions([]);
      return [];
    } catch (error) {
      toast.error("Failed to fetch roles");
      throw error;
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        await Promise.all([fetchRoles()]);
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    };

    initializeData();

    if (id) {
      setIsEditing(true);
      fetchUserDetails(id);
    } else {
      setUserData(null);
      setIsEditing(false);
      setUserStatus("");
    }
  }, [id]);

  useEffect(() => {
    updateFilteredRoles();
  }, [rolesOptions, userData]);

  const updateFilteredRoles = (selectedApps = []) => {
    setFilteredRolesOptions(rolesOptions);
  };

  const fetchUserDetails = async (userId) => {
    setIsLoading(true);
    try {
      const response = await getUser(userId);
      if (response.data) {
        const statusFromAPI = (response.data.status || "").toLowerCase(); // ✅ normalize to lowercase
        setUserStatus(statusFromAPI); // ✅ uncommented — now userStatus is properly set

        /* `password` has to be set explicitly, not left to the spread.
           ...response.data below carries whatever the service returns,
           and if that ever includes the stored password the box would
           show the hash and saving would post it back to be hashed a
           second time -- which locks the account out of its own
           credentials. It reads "" normally, meaning "leave it alone",
           and the readable password only under DEV_MODE. */
        const devPw = maySetPassword ? prefillPassword(response.data) : "";
        setPrefilledPw(devPw);

        const userData = {
          ...response.data,
          profileIds: Array.isArray(response.data.profileIds)
            ? response.data.profileIds
            : response.data.profileIds.split(","),
          status: statusFromAPI, // ✅ lowercase status in form initial values
          password: devPw,
        };
        setUserData(userData);
        updateFilteredRoles();
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error(`Failed to fetch user details: ${error.message}`);
      setUserData(null);
      setUserStatus("");
    } finally {
      setIsLoading(false);
    }
  };

  /* The detail rules on their own, and the whole form's built from
     them. There is only one step here, but the split keeps the Save
     gate and the schema reading from one object rather than two. */
  const detailSchema = Yup.object().shape({
    firstName: Yup.string().trim().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobileNumber: Yup.string()
      .matches(/^[0-9]+$/, "Must be only digits")
      .min(10, "Must be at least 10 digits")
      .required("Contact number is required"),
    ...(isEditing && {
      status: Yup.string()
        .oneOf(["unverified", "active", "inactive"], "Invalid status")
        .required("Status is required"),
    }),
    /* Optional on an edit -- blank means "leave the password alone" --
       and only for the admin who has the field, since a rule on a box
       nobody can see is a save that fails with its error off screen.
       The same rule the /setting/List-user dialog applies.

       A DEV_MODE prefill is exempt: it is an existing password, it is
       not being changed, and it may be shorter than the rule. */
    ...(isEditing && maySetPassword && {
      password: Yup.string()
        .transform((v) => (typeof v === "string" && v.trim() === "" ? undefined : v))
        .test(
          "pw-length",
          "Use at least 8 characters",
          (v) => v === undefined || v === prefilledPw || String(v).length >= 8
        ),
    }),
  });

  const validationSchema = detailSchema.shape({
    profileIds: Yup.array().min(1, "At least one role must be selected"),
  });

  const initialValues = userData || {
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    password: "",
    profileIds: [],
    status: "",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        mobileNumber: values.mobileNumber,
        profileIds: values.profileIds.join(","),
      };

      /* The key stays off the payload unless a password was actually
         typed by someone allowed to set one:
         - nothing typed is the ordinary edit, "leave it alone";
         - an edit by a non-admin never had the field, so any value on
           `values` is stale rather than intended. Creating an account
           is not gated -- that password is part of creating it;
         - a DEV_MODE prefill left exactly as it came back is not a
           change, and does not need re-sending. */
      const newPassword = String(values.password || "").trim();
      const untouchedPrefill = Boolean(prefilledPw) && newPassword === prefilledPw;
      const mayPost = !isEditing || maySetPassword;
      if (newPassword.length > 3 && mayPost && !untouchedPrefill) {
        payload.password = newPassword;
      }

      if (values.status) {
        payload.status = values.status.toLowerCase();
      }

      console.log("Payload being sent:", payload);

      if (isEditing && id) {
        await updateUser(payload, id);
        toast.success("User updated successfully!");
      } else {
        await addUser(payload);
        toast.success("User added successfully!");
      }

      resetForm();
      finish();
    } catch (error) {
      console.error("Error submitting user:", error);
      // auth-api explains a refusal (e.g. a duplicate email or mobile
      // number) in message -- a string, or { message } from its locales.
      const serverMessage = error?.response?.data?.message;
      toast.error(
        (typeof serverMessage === "string" ? serverMessage : serverMessage?.message) ||
          `Error processing user: ${error.message}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const refreshRoles = async () => {
    try {
      await fetchRoles();
      // A refresh, not a saved change: it toasts even inside the dialog.
      toast.success("Roles refreshed successfully!", { toast: true });
    } catch (error) {
      console.error("Error refreshing roles:", error);
      toast.error("Failed to refresh roles");
    }
  };

  // ✅ Trim on paste for text fields
  const handleTrimmedPaste = (e, fieldName, setFieldValue) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    const trimmed = pasted.trim();
    setFieldValue(fieldName, trimmed);
  };

  if (isLoading) {
    if (embedded) {
      return (
        <div className="sf">
          <div className="sf-empty">Loading student…</div>
        </div>
      );
    }
    return <div className="text-center p-5">Loading user data...</div>;
  }

  /* ── The dialog build ───────────────────────────────────────────────
     One step, not the two the /setting/List-user dialog uses. That form
     splits because its second question -- which roles -- is genuinely
     open. Here the answer is already given: fetchRoles finds the Student
     role and pre-selects it on a new account, so a second step would be
     a screen showing work already done. The roles fold away instead,
     named in the summary, open to change when someone needs to.

     The field language is the `sf-*` one the stepped dialogs use, so
     this dialog and those read as the same family.
     ------------------------------------------------------------------ */
  if (embedded) {
    return (
      <div className="sf">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldTouched,
            submitForm,
            isSubmitting,
          }) => {
            const bad = (name) => touched[name] && errors[name];

            /* What the account's roles are called, for the read-only line
               in the details. Held roles that fetchRoles has not returned
               are left out rather than shown as a raw id -- on a new
               account it is simply Student, which fetchRoles ticks. */
            const chosen = new Set((values.profileIds || []).map(String));
            const chosenNames = rolesOptions
              .filter((r) => chosen.has(String(r.id)))
              .map((r) => r.label);

            // The form is answered when its own fields pass their own
            // rules -- the same object the full schema is built from, so
            // there is no second copy of them here to fall out of step.
            const stepOneOk = detailSchema.isValidSync(values);

            const touchStepOne = () => {
              ["firstName", "lastName", "email", "mobileNumber"].forEach((n) =>
                setFieldTouched(n, true, true)
              );
              if (isEditing) setFieldTouched("status", true, true);
            };

            const save = () => {
              // Touch everything first: an untouched field's error is
              // computed but not shown, so a save that fails validation
              // would otherwise do nothing visible at all.
              setFieldTouched("profileIds", true, true);
              touchStepOne();
              if (!stepOneOk) return;
              submitForm();
            };

            const field = (name, label, extra = {}) => (
              <div className="sf-field">
                <div className="sf-label-row">
                  <label className="sf-label" htmlFor={`stu-${name}`}>
                    {label}
                    {extra.required !== false && <span className="sf-req">*</span>}
                  </label>
                </div>
                {extra.reveal ? (
                  <div className="sf-input-wrap">
                    <input
                      id={`stu-${name}`}
                      name={name}
                      type={showPassword ? "text" : "password"}
                      className={`sf-input${bad(name) ? " sf-input--bad" : ""}`}
                      value={values[name] || ""}
                      placeholder={extra.placeholder}
                      autoComplete="new-password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <button
                      type="button"
                      className="sf-reveal"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide" : "Show"}
                    >
                      <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                    </button>
                  </div>
                ) : (
                  <input
                    id={`stu-${name}`}
                    name={name}
                    type={extra.type || "text"}
                    className={`sf-input${bad(name) ? " sf-input--bad" : ""}`}
                    value={values[name] || ""}
                    placeholder={extra.placeholder}
                    inputMode={extra.inputMode}
                    autoComplete="off"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                )}
                {bad(name) ? (
                  <p className="sf-error"><WarnIcon />{errors[name]}</p>
                ) : (
                  extra.hint && <p className="sf-hint">{extra.hint}</p>
                )}
              </div>
            );

            return (
              <Form
                noValidate
                onSubmit={(e) => {
                  // Nothing in here is type="submit", so the only thing
                  // that reaches this is Enter pressed inside a field.
                  // With one step, that key means "save".
                  e.preventDefault();
                  save();
                }}
              >
                {/* No stepper: one step needs no tabs to move between.
                    The stage wrapper stays -- it carries the dialog's
                    entrance animation and its min-height. */}
                <div className="att-stage is-in">
                  <div className="att-stage-inner">
                    {(
                      <section className="att-step">
                <div className="sf-row">
                  {field("firstName", "First name", { placeholder: "Asha" })}
                  {field("lastName", "Last name", { placeholder: "Kulkarni" })}
                </div>

                <div className="sf-row">
                  {field("email", "Email", {
                    type: "email",
                    placeholder: "asha@example.com",
                    hint: "Also the username they sign in with.",
                  })}
                  {field("mobileNumber", "Contact number", {
                    inputMode: "numeric",
                    placeholder: "9876543210",
                    hint: "Digits only, at least ten.",
                  })}
                </div>

                {/* A new account gets a password; an existing one gets its
                    status and a box to set a new password. */}
                {isEditing ? (
                  <>
                  <div className="sf-field">
                    <div className="sf-label-row">
                      <label className="sf-label" htmlFor="stu-status">
                        Status<span className="sf-req">*</span>
                      </label>
                    </div>
                    <select
                      id="stu-status"
                      name="status"
                      className={`sf-input${bad("status") ? " sf-input--bad" : ""}`}
                      value={values.status || userStatus || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <option value="">Select a status…</option>
                      {statusOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {bad("status") ? (
                      <p className="sf-error"><WarnIcon />{errors.status}</p>
                    ) : (
                      <p className="sf-hint">
                        Inactive keeps the account but refuses the sign-in.
                      </p>
                    )}
                  </div>

                  {/* Set a password directly. Open to everyone who reaches
                      this form -- see maySetPassword.

                      Blank is the normal case and means "leave it alone":
                      the box cannot show the current password (it is
                      hashed), so an empty one must not read as "no
                      password". Under DEV_MODE it arrives already filled,
                      when the record came back carrying a readable one. */}
                  {maySetPassword &&
                    field("password", "New password", {
                      reveal: true,
                      required: false,
                      placeholder: prefilledPw
                        ? undefined
                        : "Leave blank to keep the current one",
                      hint: prefilledPw
                        ? "DEV_MODE: this is the account's current password. Leave it as it is to keep it, or type over it to change it."
                        : "Replaces this student's password immediately. They are not told — you will need to pass it on.",
                    })}
                  </>
                ) : (
                  field("password", "Password", {
                    reveal: true,
                    required: false,
                    placeholder: "Leave blank to send an invitation",
                    hint: "Set one now, or leave it blank and let them choose it.",
                  })
                )}

                {/* The role, shown rather than asked. Every account on
                    /student-users is a student, so there is nothing to
                    choose -- but it is still worth seeing, and an account
                    carrying a second role should say so rather than look
                    identical to one that does not. Read-only: the roles
                    step that used to set it is gone, and `profileIds` goes
                    to the payload exactly as it arrived. */}
                <div className="sf-field">
                  <div className="sf-label-row">
                    <label className="sf-label">
                      {chosenNames.length > 1 ? "Roles" : "Role"}
                    </label>
                  </div>
                  <div className="stu-roleline">
                    {chosenNames.length > 0 ? (
                      chosenNames.map((name) => (
                        <span className="sf-state sf-state--auto" key={name}>
                          {name}
                        </span>
                      ))
                    ) : (
                      <span className="sf-state sf-state--auto">Student</span>
                    )}
                  </div>
                  {touched.profileIds && errors.profileIds ? (
                    <p className="sf-error"><WarnIcon />{errors.profileIds}</p>
                  ) : (
                    <p className="sf-hint">
                      {isEditing
                        ? "Set when the account was created. Change it from Settings › Users."
                        : "Granted automatically — every account added here is a student."}
                    </p>
                  )}
                </div>

                      </section>
                    )}
                  </div>
                </div>

                <footer className="sf-foot">
                  {/* One step, so one pair: leave, or save. Not type="submit"
                      -- the Form's onSubmit handles Enter and routes it here
                      itself. */}
                  <button type="button" className="sf-btn" onClick={leave}>
                    Cancel
                  </button>

                  <span className="sf-foot-gap" />

                  <button
                    type="button"
                    className="sf-btn sf-btn--main"
                    /* Not gated on profileIds any more. The roles step is
                       gone, so an empty one is not something the form can
                       be used to fix -- a dead Save button with no visible
                       cause would just be a dialog that cannot be closed by
                       saving. save() surfaces it as a field error instead. */
                    disabled={isSubmitting || !stepOneOk}
                    onClick={save}
                  >
                    {isSubmitting && <span className="sf-spin" aria-hidden="true" />}
                    {isSubmitting
                      ? isEditing ? "Updating…" : "Saving…"
                      : isEditing ? "Update student" : "Create student"}
                  </button>
                </footer>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }

  return (
    <div>
      <Link to="/student-users">
        <Button variant="primary mb-4">Go to User List</Button>
      </Link>
      <h4>{isEditing ? "Edit User" : "Add User"}</h4>

      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <Form className="p-4" onSubmit={handleSubmit}>
            <Card className="p-4">
              <Row className="mb-3 me-3">
                <Col md={5} className="me-5">
                  <TextInput
                    label="First Name"
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.firstName && errors.firstName}
                    errorMessage={errors.firstName}
                    onPaste={(e) =>
                      handleTrimmedPaste(e, "firstName", setFieldValue)
                    }
                  />
                </Col>
                <Col md={5}>
                  <TextInput
                    label="Last Name"
                    name="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.lastName && errors.lastName}
                    errorMessage={errors.lastName}
                    onPaste={(e) =>
                      handleTrimmedPaste(e, "lastName", setFieldValue)
                    }
                  />
                </Col>
              </Row>
              <Row className="mb-3 me-3">
                <Col md={5} className="me-5">
                  <TextInput
                    label="Email"
                    name="email"
                    type="email"
                    autoComplete="new-password"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && errors.email}
                    errorMessage={errors.email}
                    onPaste={(e) =>
                      handleTrimmedPaste(e, "email", setFieldValue)
                    }
                  />
                </Col>
                <Col md={5}>
                  {/* ✅ onPaste added for mobileNumber */}
                  <TextInput
                    label="Mobile Number"
                    name="mobileNumber"
                    value={values.mobileNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.mobileNumber && errors.mobileNumber}
                    errorMessage={errors.mobileNumber}
                    onPaste={(e) =>
                      handleTrimmedPaste(e, "mobileNumber", setFieldValue)
                    }
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                {/* Open to everyone who reaches this form, on a new account
                    and an existing one alike -- see maySetPassword. */}
                {(!isEditing || maySetPassword) && (
                <Col md={5} className="me-5">
                  {/* ✅ Password with eye toggle */}
                  <Form.Group controlId="password">
                    <Form.Label>Password</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        name="password"
                        autoComplete="new-password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.password && errors.password}
                        style={{ paddingRight: "40px" }}
                      />
                      <span
                        onClick={() => setShowPassword((prev) => !prev)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "#6c757d",
                          zIndex: 10,
                        }}
                      >
                        <i
                          className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                        />
                      </span>
                      {touched.password && errors.password && (
                        <Form.Control.Feedback type="invalid">
                          {errors.password}
                        </Form.Control.Feedback>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                )}

                <Col md={5} className="me-5">
                  <Form.Group controlId="status">
                    <Form.Label>Status</Form.Label>
                    {/* ✅ value now purely driven by Formik values.status */}
                    <Form.Select
                      name="status"
                      value={values.status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.status && errors.status}
                    >
                      <option value="">Select Status</option>
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    {touched.status && errors.status && (
                      <Form.Control.Feedback type="invalid">
                        {errors.status}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>

                <Col md={5} className="me-5">
                  <label>Roles</label>
                  <AsyncSelectDropdown
                    options={filteredRolesOptions}
                    value={(values.profileIds || []).map((id) => {
                      const role = rolesOptions.find(
                        (option) => option.id === id,
                      );
                      return role || null;
                    })}
                    onChange={(selectedOptions) => {
                      const selectedValues = Array.isArray(selectedOptions)
                        ? selectedOptions.map((option) => option.id)
                        : selectedOptions
                          ? [selectedOptions.id]
                          : [];
                      setFieldValue("profileIds", selectedValues);
                    }}
                    placeholder="Select profiles"
                    isMulti
                    onRefresh={refreshRoles}
                    isDisabled={true}
                    isClearable={false}
                    components={{ MultiValueRemove: () => null }}
                  />
                  {touched.profileIds && errors.profileIds && (
                    <div className="text-danger">{errors.profileIds}</div>
                  )}
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={3} className="text-start">
                  <Link to="/student-users">
                    <Button variant="danger" size="sm">
                      Cancel
                    </Button>
                  </Link>
                </Col>
                <Col className="text-end">
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isSubmitting}
                    className="me-3"
                  >
                    {isSubmitting
                      ? "Processing..."
                      : isEditing
                        ? "Update"
                        : "Submit"}
                  </Button>
                </Col>
              </Row>
            </Card>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddStudentUser;
